import { Controller } from "@hotwired/stimulus";
import { actionSource } from "../utils/action_source";
import { hasModifierChord, isReservedArrowChord, logicalArrowStep } from "../utils/arrow_step";
import { AttributeLease } from "../utils/attribute_lease";
import { setAttributeIfChanged } from "../utils/attribute_write";
import { DetachGate } from "../utils/detach_gate";
import { ownerIndex } from "../utils/event_owner";
import { HoverRecheck } from "../utils/hover_recheck";
import { ListenerSet } from "../utils/listener_set";
import { MorphRenderWatcher } from "../utils/morph_render_watcher";
import { MoveCounter } from "../utils/move_counter";
import { NUMBER_BOUNDS, type NumberValueConstraints } from "../utils/number_bounds";
import { NumberValueReader } from "../utils/number_value";
import { prefersReducedMotion } from "../utils/reduced_motion";
import { RovingTabindex, rovingMove } from "../utils/roving_tabindex";
import { SafeInterval } from "../utils/safe_timeout";
import { type StateReason, stateReasonFor } from "../utils/state_reason";
import { StateRegions } from "../utils/state_regions";
import { targetSelector } from "../utils/target_selector";

/** Advance delay used when `interval` is not a finite positive number. */
const DEFAULT_INTERVAL = 5000;

/**
 * State attributes whose in-place rewrite changes what the carousel shows without
 * any target connecting or disconnecting — the shape a Turbo morph takes when it
 * keeps the elements and swaps only their attributes.
 */
const OBSERVED_ATTRIBUTES = ["aria-selected", "data-state", "hidden"];

/**
 * Headless, accessible **Carousel** (slideshow) behavior.
 *
 * Stepping reads the current slide markup and compares with the last published
 * position. Slide and rotation controls settle before notification; synchronous
 * replacement navigation suppresses any older slide confirmation still pending.
 *
 * Markup contract (identifier: `stimeo--carousel`):
 *   <section data-controller="stimeo--carousel" aria-roledescription="carousel"
 *            aria-label="Featured"
 *            data-stimeo--carousel-autoplay-value="false"
 *            data-stimeo--carousel-interval-value="5000"
 *            data-stimeo--carousel-loop-value="true">
 *     <button aria-label="Slide autoplay" data-stimeo--carousel-target="playToggle">
 *       <span aria-hidden="true" data-stimeo--carousel-target="onLabel" hidden>❚❚</span>
 *       <span aria-hidden="true" data-stimeo--carousel-target="offLabel">▶</span>
 *     </button>
 *     <div data-stimeo--carousel-target="viewport">
 *       <div role="tabpanel" data-stimeo--carousel-target="slide">…</div>
 *       <div role="tabpanel" data-stimeo--carousel-target="slide" hidden inert>…</div>
 *     </div>
 *     <button data-stimeo--carousel-target="prev">‹</button>
 *     <button data-stimeo--carousel-target="next">›</button>
 *     <div role="tablist" data-stimeo--carousel-target="pickerList">
 *       <button role="tab" data-stimeo--carousel-target="picker"></button>
 *     </div>
 *   </section>
 *
 * Implements the WAI-ARIA APG **Carousel** pattern. With pickers it is the tabbed
 * variant: each picker is a `tab` carrying `aria-selected` and the single roving
 * `tabindex`, and each slide is its `tabpanel`. Without pickers the slides are
 * `group`s and `prev`/`next` drive them. The current slide is exposed through
 * `data-state` (`active`/`inactive`); the rest carry `hidden` **and** `inert`, so
 * they stay out of the focus order and the accessibility tree even when consumer
 * CSS overrides `display` to lay the slides out as a track.
 *
 * @remarks
 * Behavior only — transitions, layout, and visuals are the consumer's CSS.
 *
 * **The controller owns its own wiring.** Clicks, picker keys, hover, and focus
 * are delegated from the controller element, so no `data-action` is required and
 * a target added at runtime works the moment it appears. Explicit `data-action`
 * bindings to the declared actions also work and coexist with the delegated
 * path: the binding runs first and marks the event, and the delegated listener
 * stands down, so one interaction is never handled twice.
 *
 * **`autoplay` is the single source of truth for the rotation intent.** The
 * toggle writes back to it, so the state survives a Turbo Drive cache restore
 * without a second, competing signal; `aria-pressed` is a pure output the
 * controller owns, as is the `onLabel`/`offLabel` pair a toggle may carry: each
 * toggle shows the half its rotation state belongs to, and a half with no partner
 * inside the same toggle keeps the visibility the consumer authored. The toggle's
 * name comes from `aria-label` and holds in both states, so the pair is a visual
 * affordance the accessibility tree does not read. Rotation is suspended — not
 * cancelled — while the pointer rests on the carousel, while focus is inside it,
 * and while the tab is hidden; each suspension lifts on its own (WCAG 2.2.2 is met
 * by the toggle, which is the one control that stops rotation for good). A carousel
 * with nothing left to advance to (one slide, or the last slide of a non-looping
 * set) shows the intent as off and marks the toggle `aria-disabled`, without
 * rewriting `autoplay`: once a slide lies ahead again, the intent takes effect
 * again. `prefers-reduced-motion` holds back the intent `autoplay` declares at
 * connect the same way, until the user presses the toggle or the page changes
 * `autoplay`.
 *
 * The rotation ends when the controller really leaves — a Turbo navigation, a
 * removed element, `data-controller` dropping it — and a carousel still in the
 * document hears that end as `pause`. An in-page move reconnects the same
 * instance and keeps the run: no edge, no restarted interval. Picker arrow keys,
 * `Home`, and `End` move focus only (manual activation); slide changes never steal focus
 * from the control the user operated. Picker arrow keys follow the writing
 * direction of the `pickerList` target, or of the controller element without one.
 * A slide change the user drove — including an
 * autoplay tick they started — is reported as `stimeo--carousel:change` with
 * `{ index, total, reason }`. `stimeo--carousel:reconcile` carries `{ index, total }`
 * when the controller re-derives the position itself, whether a target came or went or
 * a retained element's state attributes were rewritten in place.
 */
export class CarouselController extends Controller<HTMLElement> {
  /** Numeric read boundaries share one reader for this controller instance. */
  readonly #numbers = new NumberValueReader();

  /** Identifies the latest published state transition. */
  readonly #moves = new MoveCounter();

  static override targets = [
    "slide",
    "viewport",
    "prev",
    "next",
    "picker",
    "pickerList",
    "playToggle",
    "onLabel",
    "offLabel",
  ];
  static override values = {
    autoplay: { type: Boolean, default: false },
    interval: { type: Number, default: DEFAULT_INTERVAL },
    loop: { type: Boolean, default: true },
  };

  static valueConstraints = {
    interval: NUMBER_BOUNDS.positiveTimer,
  } satisfies NumberValueConstraints<typeof CarouselController.values>;
  static actions = [
    "goto",
    "next",
    "onPickerKeydown",
    "pause",
    "prev",
    "resume",
    "togglePlay",
  ] as const;
  static events = ["change", "pause", "play", "reconcile"] as const;

  declare readonly slideTargets: HTMLElement[];
  declare readonly pickerTargets: HTMLElement[];
  declare readonly pickerListTarget: HTMLElement;
  declare readonly hasPickerListTarget: boolean;
  declare readonly viewportTarget: HTMLElement;
  declare readonly viewportTargets: HTMLElement[];
  declare readonly hasViewportTarget: boolean;
  declare readonly prevTargets: HTMLElement[];
  declare readonly nextTargets: HTMLElement[];
  declare readonly playToggleTargets: HTMLElement[];
  declare readonly onLabelTargets: HTMLElement[];
  declare readonly offLabelTargets: HTMLElement[];
  declare autoplayValue: boolean;
  declare intervalValue: number;
  declare loopValue: boolean;

  readonly #roving = new RovingTabindex(() => this.pickerTargets);
  readonly #reconcileTargets = new MorphRenderWatcher(() => this.#reconcileTargetSet());
  readonly #intervals = new SafeInterval();
  /** Unreachable step controls and the toggle of a carousel that cannot rotate. */
  readonly #ariaDisabled = new AttributeLease<HTMLElement>("aria-disabled", this.identifier);
  /** The play toggle's pressed state, a pure output of the rotation intent. */
  readonly #ariaPressed = new AttributeLease<HTMLElement>("aria-pressed", this.identifier);
  /** The slide container's live-region politeness, which tracks the rotation. */
  readonly #ariaLive = new AttributeLease<HTMLElement>("aria-live", this.identifier);
  /** Pairs with {@link CarouselController.#ariaLive}: only the changed slide is read. */
  readonly #ariaAtomic = new AttributeLease<HTMLElement>("aria-atomic", this.identifier);
  /** The label pair a play toggle may carry, one half per rotation state. */
  readonly #labels = new StateRegions(
    {
      whenTrue: () => this.onLabelTargets,
      whenFalse: () => this.offLabelTargets,
    },
    this.identifier,
  );
  /** Tells an in-page move, which keeps the rotation, from a real detach. */
  readonly #gate = new DetachGate();
  /** The next pointer movement, listened for while a pointer suspension waits on it. */
  readonly #pointer = new HoverRecheck(this.element.ownerDocument, () => {
    this.#pointerPaused = false;
    this.#syncTimer();
  });
  /**
   * Events an authored action binding already took. The delegated listener runs
   * later — it sits on the controller element, above every control — so it can
   * consume the mark and stand down, letting the two wirings coexist without
   * handling one interaction twice.
   */
  readonly #handledEvents = new WeakSet<Event>();

  /**
   * Whether `connect()` has run. Scheduling is already inert outside that window
   * (`MorphRenderWatcher`), so this gates the Value callbacks Stimulus
   * delivers ahead of `connect()`.
   */
  #connected = false;
  /** Index of the visible slide. */
  #index = 0;
  /** The visible slide element, which identifies it across a changing target set. */
  #activeSlide: HTMLElement | null = null;
  /** Slide count at the last resolved state, so a changed total is reportable. */
  #total = 0;
  /**
   * Pointer rests on the carousel: a suspension that lifts on `mouseleave`, or on the
   * pointer movement that confirms a suspension a move or a connect left unconfirmed.
   */
  #pointerPaused = false;
  /** Focus is inside the carousel: a suspension that lifts when it leaves. */
  #focusPaused = false;
  /** The tab is in the background: a suspension that lifts when it returns. */
  #hiddenPaused = false;
  /** Reduced motion holds back the intent declared at connect, until a press or a new one. */
  #motionHold = false;
  /** Id of the live autoplay interval, or null when stopped. */
  #timerId: number | null = null;
  /** Delay the live interval was armed with, so an `interval` change re-arms it. */
  #timerInterval = 0;
  /** Follows state-attribute rewrites on retained slides and pickers. */
  #observer: MutationObserver | null = null;

  /** Keeps a hidden tab from advancing behind the user's back. */
  readonly #onVisibilityChange = (): void => {
    this.#hiddenPaused = document.visibilityState === "hidden";
    this.#syncTimer();
  };

  /** The listeners of one connection, released together on `disconnect()`. */
  readonly #listeners = new ListenerSet();

  /**
   * Renders the initial slide, wires the delegated listeners, and starts autoplay
   * when requested.
   *
   * Focus, the tab's visibility and the reduced-motion hold are read afresh. The
   * pointer is not something the document can answer yet: no `mouseleave` reaches a
   * node that moved, and `:hover` can answer from before the move until the pointer
   * moves again. So an in-page move — which Stimulus delivers to the *same* controller
   * instance as `disconnect()` then `connect()` — keeps the pointer suspension it had,
   * `:hover` reading true adds one, and either waits for the next pointer movement to
   * be confirmed or lifted. A rotation the move carried is still running, so the edges
   * are judged against it. The attribute observer starts after the first render so the
   * controller's own opening writes are not fed back to it.
   */
  override connect(): void {
    const moved = this.#gate.pending;
    this.#gate.cancel();
    this.#pointerPaused = (moved && this.#pointerPaused) || this.element.matches(":hover");
    if (this.#pointerPaused) this.#pointer.wait(this.element);
    this.#focusPaused = this.element.contains(document.activeElement);
    this.#hiddenPaused = document.visibilityState === "hidden";
    this.#motionHold = prefersReducedMotion();
    this.#index = this.#resolveIndex();
    this.#render({ focus: false });
    this.#total = this.slideTargets.length;
    this.#syncTimer();
    this.#connected = true;
    this.#reconcileTargets.observe(this.element);

    this.#listeners.add(this.element, "click", this.#onClick);
    this.#listeners.add(this.element, "keydown", this.#onKeydown);
    this.#listeners.add(this.element, "focusin", this.#onFocusin);
    this.#listeners.add(this.element, "focusout", this.#onFocusout);
    this.#listeners.add(this.element, "mouseenter", this.#onMouseenter);
    this.#listeners.add(this.element, "mouseleave", this.#onMouseleave);
    this.#listeners.add(document, "visibilitychange", this.#onVisibilityChange);
    this.#observer = new MutationObserver(this.#onAttributeMutations);
    this.#observer.observe(this.element, {
      subtree: true,
      attributes: true,
      attributeFilter: OBSERVED_ATTRIBUTES,
    });
  }

  /**
   * Releases every listener and observer, returns the leased ARIA, drops the focus
   * and tab suspensions. The rotation goes through the detach gate: a reconnect in
   * the same batch — an in-page move — keeps it, and the pointer suspension with it;
   * anything else ends it.
   */
  override disconnect(): void {
    this.#connected = false;
    this.#pointer.dispose();
    this.#listeners.dispose();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#reconcileTargets.disconnect();
    this.#focusPaused = false;
    this.#hiddenPaused = false;
    this.#activeSlide = null;
    this.#returnLeases();
    this.#gate.disconnected(this, () => this.#endRun());
  }

  /**
   * Re-establishes the single selected picker when one is added after connect.
   *
   * Without this an appended picker that arrives `aria-selected="true"` leaves two
   * marked at once — the authored pre-selection is only read on connect, so
   * nothing else ever resolves the conflict. The current slide is kept: the
   * repaint re-derives every picker from `#index`, so a late arrival never steals
   * the selection.
   */
  pickerTargetConnected(picker: HTMLElement): void {
    picker.tabIndex = -1;
    this.#reconcileTargets.schedule();
  }

  /** Repairs selection and roving after a picker leaves a retained carousel. */
  pickerTargetDisconnected(): void {
    this.#reconcileTargets.schedule();
  }

  /** Reconciles a slide added in the same DOM batch as its picker. */
  slideTargetConnected(): void {
    this.#reconcileTargets.schedule();
  }

  /** Re-clamps the active index after a slide is removed. */
  slideTargetDisconnected(): void {
    this.#reconcileTargets.schedule();
  }

  /** Publishes the live region on a viewport that joins a connected carousel. */
  viewportTargetConnected(): void {
    this.#reconcileTargets.schedule();
  }

  /**
   * Gives a viewport that no longer resolves its own live region back, even after
   * `disconnect()`, and republishes it on the viewport that stays.
   */
  viewportTargetDisconnected(viewport: HTMLElement): void {
    if (!this.viewportTargets.includes(viewport)) {
      this.#ariaLive.return(viewport);
      this.#ariaAtomic.return(viewport);
    }
    this.#reconcileTargets.schedule();
  }

  /**
   * Gives a toggle that no longer resolves as one its label pair, its `aria-disabled` and
   * its `aria-pressed` back, even after `disconnect()`.
   */
  playToggleTargetDisconnected(toggle: HTMLElement): void {
    if (this.playToggleTargets.includes(toggle)) return;
    this.#labels.release(toggle);
    this.#ariaDisabled.return(toggle);
    this.#ariaPressed.return(toggle);
  }

  /** Gives a button that no longer resolves as a `prev` control its `aria-disabled` back. */
  prevTargetDisconnected(button: HTMLElement): void {
    if (!this.prevTargets.includes(button)) this.#ariaDisabled.return(button);
  }

  /** Gives a button that no longer resolves as a `next` control its `aria-disabled` back. */
  nextTargetDisconnected(button: HTMLElement): void {
    if (!this.nextTargets.includes(button)) this.#ariaDisabled.return(button);
  }

  /**
   * Publishes the rotation state on a toggle that joins a connected carousel.
   *
   * A toggle the markup already carries connects ahead of `connect()`, where the
   * suspensions and the reduced-motion hold are not settled yet; the opening pass
   * covers it.
   */
  playToggleTargetConnected(): void {
    if (this.#connected) this.#syncTimer();
  }

  /**
   * Publishes the rotation state on a label half that joins a connected toggle.
   *
   * A half arriving changes the toggle's subtree, not its attributes, so the
   * attribute observer never sees it; where the arrival completes a pair that was
   * standing alone, this is what makes that pair whole and reflectable.
   */
  onLabelTargetConnected(): void {
    if (this.#connected) this.#syncTimer();
  }

  /** Publishes the rotation state on a label half that joins a connected toggle. */
  offLabelTargetConnected(): void {
    if (this.#connected) this.#syncTimer();
  }

  /** Follows a rotation intent the page changed at runtime, lifting the reduced-motion hold. */
  autoplayValueChanged(): void {
    if (!this.#connected) return;
    this.#motionHold = false;
    this.#syncTimer();
  }

  /** Re-arms the live interval at the new delay without reporting a state change. */
  intervalValueChanged(): void {
    if (this.#connected) this.#syncTimer();
  }

  /**
   * Re-publishes the step controls and re-evaluates the non-looping end through the
   * reconcile pass, which reports a move only when the position changed.
   */
  loopValueChanged(): void {
    this.#reconcileTargets.schedule();
  }

  /** Advances to the next slide. Delegated; `data-action` wiring is optional. */
  next(event?: Event): void {
    if (event?.defaultPrevented) return;
    this.#markHandled(event);
    this.#select(this.#step(1), { focus: false }, stateReasonFor(event));
  }

  /** Returns to the previous slide. Delegated; `data-action` wiring is optional. */
  prev(event?: Event): void {
    if (event?.defaultPrevented) return;
    this.#markHandled(event);
    this.#select(this.#step(-1), { focus: false }, stateReasonFor(event));
  }

  /** Jumps to the slide of an owned picker or descendant, without moving DOM focus. */
  goto(source: Event | HTMLElement): void {
    const { event, host, origin, reason } = actionSource(source);
    if (origin && origin.closest(`[data-controller~="${this.identifier}"]`) !== this.element)
      return;
    if (event?.defaultPrevented) return;
    const picker = host?.closest<HTMLElement>(targetSelector(this.identifier, "picker"));
    if (!picker || !this.pickerTargets.includes(picker)) return;
    this.#markHandled(event ?? undefined);
    this.#select(this.pickerTargets.indexOf(picker), { focus: false }, reason);
  }

  /**
   * Flips the rotation intent the toggle shows, on the user's explicit request.
   *
   * The result is written to the `autoplay` Value, which is where every other path
   * reads it from. A carousel with nothing to advance to has no intent to flip: the
   * toggle is marked `aria-disabled` and does nothing.
   */
  togglePlay(event?: Event): void {
    if (event?.defaultPrevented) return;
    this.#markHandled(event);
    this.#togglePlay();
  }

  /**
   * Suspends rotation. A `focus`-family event records that focus is inside the
   * carousel; anything else — hover, or a bare programmatic call — records the
   * pointer suspension. Both lift through the matching {@link resume}, and
   * neither touches the rotation intent.
   */
  pause(event?: Event): void {
    this.#markHandled(event);
    if (isFocusEvent(event)) this.#focusPaused = true;
    else this.#setPointerPaused(true);
    this.#syncTimer();
  }

  /**
   * Lifts the matching suspension. A `focusout` whose `relatedTarget` is still
   * inside the carousel is focus moving between its own controls, which leaves the
   * focus suspension in place — releasing it there would stop and restart the
   * interval on every Tab press.
   */
  resume(event?: Event): void {
    this.#markHandled(event);
    if (isFocusEvent(event)) {
      if (this.#focusStaysInside(event)) return;
      this.#focusPaused = false;
    } else {
      this.#setPointerPaused(false);
    }
    this.#syncTimer();
  }

  /** Picker roving for authored bindings; the delegated path is `#onKeydown`. */
  onPickerKeydown(event: KeyboardEvent): void {
    const index = this.#pickerIndexFor(event.currentTarget);
    if (index === -1) return;
    this.#markHandled(event);
    this.#handlePickerKeydown(event, index);
  }

  /** Delegated activation for pickers and step controls without authored actions. */
  readonly #onClick = (event: MouseEvent): void =>
    this.#delegate(event, () => {
      const index = this.#pickerIndexFor(event.target);
      if (index !== -1) {
        this.#select(index, { focus: false });
      } else if (hits(this.nextTargets, event.target)) {
        this.#select(this.#step(1), { focus: false });
      } else if (hits(this.prevTargets, event.target)) {
        this.#select(this.#step(-1), { focus: false });
      } else if (hits(this.playToggleTargets, event.target)) {
        this.#togglePlay();
      }
    });

  /** Delegated picker roving for pickers without authored actions. */
  readonly #onKeydown = (event: KeyboardEvent): void =>
    this.#delegate(event, () => {
      const index = this.#pickerIndexFor(event.target);
      if (index !== -1) this.#handlePickerKeydown(event, index);
    });

  /** Focus arriving anywhere inside suspends the rotation. */
  readonly #onFocusin = (event: FocusEvent): void =>
    this.#delegate(event, () => {
      this.#focusPaused = true;
      this.#syncTimer();
    });

  /** Focus genuinely leaving lifts the suspension; moves between own controls do not. */
  readonly #onFocusout = (event: FocusEvent): void =>
    this.#delegate(event, () => {
      if (this.#focusStaysInside(event)) return;
      this.#focusPaused = false;
      this.#syncTimer();
    });

  /** Pointer entry suspends the rotation. */
  readonly #onMouseenter = (event: MouseEvent): void =>
    this.#delegate(event, () => {
      this.#setPointerPaused(true);
      this.#syncTimer();
    });

  /** Pointer exit lifts the suspension. */
  readonly #onMouseleave = (event: MouseEvent): void =>
    this.#delegate(event, () => {
      this.#setPointerPaused(false);
      this.#syncTimer();
    });

  /** Records the pointer suspension; a newer answer ends any wait on the next movement. */
  #setPointerPaused(paused: boolean): void {
    this.#pointer.dispose();
    this.#pointerPaused = paused;
  }

  /**
   * Schedules one reconciliation when a state attribute is rewritten in place —
   * the only shape of change no target callback reports.
   *
   * The filter is the `attributeFilter` alone: a pass over an attribute that is
   * not a slide's or a picker's state costs a repaint that writes nothing.
   */
  readonly #onAttributeMutations = (): void => {
    this.#reconcileTargets.schedule();
  };

  /**
   * Records that an action binding took this event.
   *
   * An authored binding sits on the control, so it runs while the event is still
   * below the controller element and always precedes the delegated listener.
   * Marking is the one signal that keeps the two wirings from both acting.
   */
  #markHandled(event?: Event): void {
    if (event) this.#handledEvents.add(event);
  }

  /** Runs a delegated handler unless an action binding, or a descendant, took the event. */
  #delegate(event: Event, run: () => void): void {
    if (this.#handledEvents.delete(event) || event.defaultPrevented) return;
    run();
  }

  /** Position of the picker that is or contains `node`, or `-1` when none does. */
  #pickerIndexFor(node: EventTarget | null): number {
    return ownerIndex(this.pickerTargets, node);
  }

  /** Whether a focus transition lands on another control of this same carousel. */
  #focusStaysInside(event: FocusEvent): boolean {
    const next = event.relatedTarget;
    return next instanceof Node && this.element.contains(next);
  }

  /** Flips the rotation intent unless there is nothing to rotate to; lifts the motion hold. */
  #togglePlay(): void {
    if (!this.#canAutoplay()) return;
    const next = !this.#intent;
    this.#motionHold = false;
    this.autoplayValue = next;
    this.#syncTimer();
  }

  /** Applies the APG picker key map: arrows, Home, and End all move focus only. */
  #handlePickerKeydown(event: KeyboardEvent, current: number): void {
    // A descendant widget that already claimed the key must not ALSO move the
    // picker focus — composition depends on this yield.
    if (event.defaultPrevented) return;
    if (isReservedArrowChord(event)) return;

    const length = this.pickerTargets.length;
    // Logical, not physical. The helper reverses only the horizontal
    // pair, so folding Down/Up into the same branch stays correct.
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
      case "ArrowLeft":
      case "ArrowUp": {
        event.preventDefault();
        const step = logicalArrowStep(
          event.key,
          this.hasPickerListTarget ? this.pickerListTarget : this.element,
        );
        this.#roving.setActive(rovingMove(current, length, step, "wrap"), { focus: true });
        return;
      }
      // A chorded Home/End is a document-level shortcut (`Control+Home` scrolls
      // the page) that belongs to the browser, exactly as a chorded arrow does.
      case "Home":
        if (hasModifierChord(event)) return;
        event.preventDefault();
        this.#roving.setActive(0, { focus: true });
        return;
      case "End":
        if (hasModifierChord(event)) return;
        event.preventDefault();
        this.#roving.setActive(length - 1, { focus: true });
        return;
      default:
    }
  }

  /**
   * Resolves the index one step away from the current one, honoring `loop`.
   *
   * Bounding is {@link CarouselController.#clampToSlides}'s job alone, so an
   * empty set is allowed to fall out of the arithmetic here rather than being
   * special-cased in two places that could disagree.
   */
  #step(delta: number): number {
    const total = this.slideTargets.length;
    const next = this.#resolveIndex() + delta;
    if (this.loopValue) return (next + total) % total;
    return Math.min(total - 1, Math.max(0, next));
  }

  /**
   * Confines an index to the slide range, so no index can hide every slide.
   *
   * The single place an index is bounded: an empty set collapses to the first
   * position, which is also where a non-numeric step from that empty set lands.
   */
  #clampToSlides(index: number): number {
    const last = this.slideTargets.length - 1;
    if (last < 0) return 0;
    return Math.min(last, Math.max(0, index));
  }

  /**
   * Changes the active slide, updates state hooks, and emits `change` — but only
   * when the index or total changes, so a `next`/`prev` clamped at the end (or an
   * autoplay tick at a non-looping boundary) re-renders without a spurious event.
   */
  #select(index: number, { focus }: { focus: boolean }, reason: StateReason = "user"): void {
    const target = this.#clampToSlides(index);
    const total = this.slideTargets.length;
    const changed = target !== this.#index || total !== this.#total;
    this.#index = target;
    this.#total = total;
    const token = changed ? this.#moves.record() : null;
    this.#render({ focus });
    // Re-evaluate autoplay after every move so reaching the non-looping end stops
    // the timer (see `#syncTimer`); idempotent for moves that don't cross a boundary.
    this.#syncTimer();
    if (token !== null && this.#moves.isLatest(token)) {
      this.dispatch("change", { detail: { index: target, total, reason } });
    }
  }

  /**
   * Reflects `this.#index` onto the slides, the pickers, and the step controls.
   *
   * Every observed attribute is written only when its value actually changes: the
   * same writes are watched by {@link CarouselController.#onAttributeMutations},
   * and an unconditional rewrite would feed the controller its own output.
   *
   * @stimeoRenderRoot
   */
  #render({ focus }: { focus: boolean }): void {
    this.#activeSlide = this.slideTargets[this.#index] ?? null;
    this.slideTargets.forEach((slide, i) => {
      const active = i === this.#index;
      setAttributeIfChanged(slide, "data-state", active ? "active" : "inactive");
      if (slide.hidden !== !active) slide.hidden = !active;
      // `hidden` alone is a style a slide track's own CSS overrides; `inert` is not.
      slide.toggleAttribute("inert", !active);
    });
    // The picker set can be shorter than the slide set (a picker removed on its
    // own, or a carousel that only pickers part of its slides). Resolving the
    // selection inside the picker range keeps exactly one selected tab and one Tab
    // stop; an out-of-range index would leave every picker at `tabindex="-1"` and
    // strand the tablist outside the Tab sequence.
    const pickerIndex = Math.min(this.#index, this.pickerTargets.length - 1);
    this.pickerTargets.forEach((picker, i) => {
      setAttributeIfChanged(picker, "aria-selected", i === pickerIndex ? "true" : "false");
    });
    this.#syncStepControls();
    this.#roving.setActive(pickerIndex, { focus });
  }

  /** Marks the step control a non-looping carousel has no slide left to reach. */
  #syncStepControls(): void {
    const last = this.slideTargets.length - 1;
    const atStart = !this.loopValue && this.#index <= 0;
    const atEnd = !this.loopValue && this.#index >= last;
    for (const button of this.prevTargets) {
      this.#ariaDisabled.write(button, atStart ? "true" : null);
    }
    for (const button of this.nextTargets) {
      this.#ariaDisabled.write(button, atEnd ? "true" : null);
    }
  }

  /**
   * Resolves which slide is current from the strongest evidence available.
   *
   * A single `data-state="active"` is unambiguous and wins, which is what restores
   * the visible slide after a Turbo cache restore even with no pickers present.
   * Competing claims — a slide inserted already marked active — are settled by the
   * element the last render actually showed, so a newcomer never displaces what the
   * reader is looking at. With no claim at all the authored picker selection
   * decides, first in DOM order; failing that the previous position is kept.
   */
  #resolveIndex(): number {
    const slides = this.slideTargets;
    const claims: number[] = [];
    slides.forEach((slide, index) => {
      if (slide.getAttribute("data-state") === "active") claims.push(index);
    });
    if (claims.length > 1 && this.#activeSlide !== null) {
      const live = slides.indexOf(this.#activeSlide);
      if (claims.includes(live)) return live;
    }
    const claimed = claims[0];
    if (claimed !== undefined) return claimed;

    const selected = this.pickerTargets.findIndex(
      (picker) => picker.getAttribute("aria-selected") === "true",
    );
    return this.#clampToSlides(selected === -1 ? this.#index : selected);
  }

  /** Re-resolves the active slide after the target set changed and reports the move. */
  #reconcileTargetSet(): void {
    const previousIndex = this.#index;
    const previousTotal = this.#total;
    this.#index = this.#resolveIndex();
    this.#total = this.slideTargets.length;
    const changed = this.#index !== previousIndex || this.#total !== previousTotal;
    const token = changed ? this.#moves.record() : null;
    this.#render({ focus: false });
    this.#syncTimer();
    // `change` stays reserved for user navigation; re-resolving onto a surviving
    // slide, or reporting a set that grew or shrank, is this controller's decision.
    if (token !== null && this.#moves.isLatest(token)) {
      this.dispatch("reconcile", { detail: { index: this.#index, total: this.#total } });
    }
  }

  /** The rotation intent in effect: `autoplay`, unless reduced motion holds it back. */
  get #intent(): boolean {
    return this.autoplayValue && !this.#motionHold;
  }

  /** Whether autoplay has anywhere left to advance to. */
  #canAutoplay(): boolean {
    const total = this.slideTargets.length;
    if (total <= 1) return false;
    return this.loopValue || this.#index < total - 1;
  }

  /** The advance delay; a non-finite or non-positive declaration falls back. */
  get #interval(): number {
    return this.#safeInterval;
  }

  /**
   * Drives the autoplay interval toward the desired state and publishes it.
   *
   * Rotation runs when the intent is on, nothing suspends it, and a slide is left
   * to advance to. The toggle shows the intent only while a slide is left, so
   * `aria-pressed` never claims a rotation that cannot happen. This body reads
   * `autoplay` and never writes it, so the declared intent returns once a slide
   * lies ahead again. Crossing the run/stop boundary emits `play`/`pause`;
   * re-arming at a new `interval` is the same state and stays silent.
   *
   * @stimeoRenderRoot
   */
  #syncTimer(): void {
    const canAutoplay = this.#canAutoplay();
    const pressed = this.#intent && canAutoplay;
    const shouldRun = pressed && !this.#pointerPaused && !this.#focusPaused && !this.#hiddenPaused;
    const wasRunning = this.#timerId !== null;
    const interval = this.#interval;

    if (this.#timerId !== null && (!shouldRun || interval !== this.#timerInterval)) {
      this.#intervals.clear(this.#timerId);
      this.#timerId = null;
    }
    if (shouldRun && this.#timerId === null) {
      this.#timerInterval = interval;
      this.#timerId = this.#intervals.set(
        () => this.#select(this.#step(1), { focus: false }, "timeout"),
        interval,
      );
    }
    // The events are edges. A consumer that subscribes after `connect()` — which
    // is every consumer on a Turbo restore, where the controller reconnects before
    // the page's own scripts run again — would miss the transition it arrived in
    // the middle of, so the same state is published as a hook it can read.
    setAttributeIfChanged(this.element, "data-state", shouldRun ? "playing" : "paused");
    for (const toggle of this.playToggleTargets) {
      this.#ariaPressed.write(toggle, pressed ? "true" : "false");
      this.#ariaDisabled.write(toggle, canAutoplay ? null : "true");
      this.#labels.reflect(toggle, pressed);
    }
    if (this.hasViewportTarget) {
      this.#ariaLive.write(this.viewportTarget, shouldRun ? "off" : "polite");
      this.#ariaAtomic.write(this.viewportTarget, "false");
    }
    if (shouldRun !== wasRunning) {
      if (shouldRun) this.dispatch("play");
      else this.dispatch("pause");
    }
  }

  /**
   * Ends the rotation once the controller has really left. A carousel still in
   * the document hears the end — `pause`, and `data-state="paused"` — so no `play`
   * it reported is left without one; a detached tree keeps the markup it had.
   */
  #endRun(): void {
    const wasRunning = this.#timerId !== null;
    this.#intervals.clearAll();
    this.#timerId = null;
    if (!wasRunning || !this.element.isConnected) return;
    setAttributeIfChanged(this.element, "data-state", "paused");
    this.dispatch("pause");
  }

  /** Hands every leased attribute back to the value the consumer authored. */
  #returnLeases(): void {
    this.#ariaDisabled.returnAll();
    this.#ariaPressed.returnAll();
    this.#ariaLive.returnAll();
    this.#ariaAtomic.returnAll();
  }
  /** Current `interval` declaration resolved against its numeric contract. */
  get #safeInterval(): number {
    return this.#numbers.read(
      this,
      "interval",
      this.intervalValue,
      CarouselController.values.interval.default,
      CarouselController.valueConstraints.interval,
    );
  }
}

/** Whether an event names a focus transition rather than a pointer one. */
function isFocusEvent(event?: Event): event is FocusEvent {
  return event?.type.startsWith("focus") === true;
}

/** Whether `node` is one of `elements` or sits inside one. */
function hits(elements: readonly HTMLElement[], node: EventTarget | null): boolean {
  return ownerIndex(elements, node) !== -1;
}
